/**
 * Wisetack brand palette and copy — the single source of truth for every
 * Wisetack-branded financing surface (settings signup card, estimate/invoice
 * context card, public consumer CTAs). Sourced from Wisetack's brand guide
 * (wisetack.com/brand) and low-code "Card spec". These are Wisetack's own
 * colors and are deliberately kept outside our semantic theme tokens so the
 * partner surfaces mirror Wisetack's branding exactly.
 */
export const WISETACK_BRAND = {
  mint: '#07C0CA',
  space: '#0E3564',
  sapphire: '#0C4E9E',
  faintMint: '#EAFAFB',
} as const;

/** Wisetack's approved merchant-signup upsell bullets, verbatim from the Card spec. */
export const WISETACK_WHY_FINANCING_POINTS = [
  'Close larger jobs',
  'Get paid up front — Wisetack pays you',
  '0% APR options for eligible customers',
] as const;

/**
 * The Card spec's title and subtitle for the merchant signup card. Wisetack
 * reviews this copy word for word, and it appears on three surfaces (the
 * Integrations card, the settings signup card, the estimate/invoice context
 * card), so it is written once here.
 */
export const WISETACK_CARD_TITLE = 'Wisetack Consumer Financing';
export const WISETACK_CARD_SUBTITLE =
  'Offer financing for larger jobs. No credit impact to check options.';

/**
 * The LEARN MORE secondary CTA target.
 *
 * Wisetack named one page for every Notifi merchant (feedback deck, slide 3),
 * not a page per partner alias: the alias identifies the signup funnel, and
 * interpolating it here produced 404s for every merchant whose alias had no
 * hosted page behind it.
 */
export const WISETACK_LEARN_MORE_URL = 'https://www.wisetack.com/partner/priceguide';

/**
 * The merchant portal host, per environment. Wisetack runs a separate
 * "-sec" domain for sandbox/mock; sending a sandbox merchant to the production
 * portal shows them an account that does not exist. Settings and the
 * estimate/invoice context card both deep-link here, so the mapping lives once.
 */
export function wisetackPortalUrl(
  environment: string | null | undefined,
  path: 'dashboard' | 'applications' | 'account-settings',
): string {
  const host =
    environment === 'production'
      ? 'https://business.wisetack.us'
      : 'https://business.wisetack-sec.us';
  return `${host}/${path}`;
}

/**
 * The partner signup URL for a merchant that has not started an application.
 * Wisetack's URL form puts the query after the hash route, and `externalId`
 * carries our tenant id so their merchant report can be matched back to us.
 * Null when the partner alias is unknown — signup is impossible without it.
 */
export function wisetackSignupUrl(settings: {
  partnerName?: string | null;
  environment?: string | null;
  merchantExternalId?: string | null;
}): string | null {
  const alias = settings.partnerName?.trim();
  if (!alias) return null;
  const host =
    settings.environment === 'production' ? 'signup.wisetack.us' : 'signup.wisetack-sec.us';
  const externalId = settings.merchantExternalId?.trim();
  return `https://${host}/#/${encodeURIComponent(alias)}${
    externalId ? `?externalId=${encodeURIComponent(externalId)}` : ''
  }`;
}
